import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Calendar,
  FileText,
  Clock,
  TrendingDown,
  Sparkles
} from 'lucide-react';

const HeroSection = ({ onStart }) => {
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    // Progressive animation stages
    const timers = [
      setTimeout(() => setAnimationStage(1), 500),
      setTimeout(() => setAnimationStage(2), 1500),
      setTimeout(() => setAnimationStage(3), 2500),
      setTimeout(() => setAnimationStage(4), 3500)
    ];

    return () => timers.forEach(clearTimeout);
  }, []);

  // Before/After comparison data
  const chaosState = {
    tasks: [
      { id: 1, text: "Brown essay", urgent: true, x: 10, y: 20 },
      { id: 2, text: "UC apps", urgent: true, x: 70, y: 15 },
      { id: 3, text: "Teacher rec", urgent: false, x: 45, y: 60 },
      { id: 4, text: "FAFSA", urgent: true, x: 80, y: 45 },
      { id: 5, text: "Test scores", urgent: false, x: 20, y: 70 },
      { id: 6, text: "Activities list", urgent: false, x: 60, y: 80 },
      { id: 7, text: "Transcripts", urgent: true, x: 35, y: 35 }
    ],
    metrics: {
      stress: 87,
      tasks: 47,
      overdue: 5,
      clarity: 23
    }
  };

  const organizedState = {
    todayFocus: "Complete profile foundation",
    upNext: "Essay brainstorming session",
    metrics: {
      stress: 34,
      tasks: 8, // batched
      overdue: 0,
      clarity: 92
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Left side - Text content */}
        <div className="space-y-6">
          {/* Tagline */}
          <div className={`inline-flex items-center gap-2 px-3 py-1 bg-azure-50 rounded-full transition-all duration-700 ${animationStage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Sparkles className="w-4 h-4 text-azure-600" />
            <span className="text-sm font-medium text-azure-700">
              Strategic College Application Management
            </span>
          </div>

          {/* Main headline */}
          <h1 className={`text-5xl lg:text-6xl font-bold text-slate-900 transition-all duration-700 delay-100 ${animationStage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Transform your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-azure-600 to-indigo-600">
              application journey
            </span>
            into a strategic advantage
          </h1>

          {/* Subheadline */}
          <p className={`text-xl text-slate-600 leading-relaxed transition-all duration-700 delay-200 ${animationStage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Let's explore how <span className="font-semibold text-slate-900">Sheppard</span>,
            an aspiring civil engineer targeting MIT and Caltech, organizes 47 application tasks
            into 8 strategic work sessions while maintaining academic excellence.
          </p>

          {/* Value props */}
          <div className={`space-y-3 transition-all duration-700 delay-300 ${animationStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700">
                <strong>Strategic sequencing</strong> identifies dependencies and optimal task order
              </span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700">
                <strong>Cognitive load management</strong> batches similar tasks for maximum efficiency
              </span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
              <span className="text-slate-700">
                <strong>Unified data architecture</strong> eliminates redundant information entry
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className={`flex items-center gap-4 transition-all duration-700 delay-400 ${animationStage >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button
              onClick={onStart}
              className="group px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-all font-medium flex items-center gap-2"
            >
              Explore Sheppard's Strategy
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="text-sm text-slate-500">
              Interactive walkthrough • 5 minute overview
            </span>
          </div>
        </div>

        {/* Right side - Visual demonstration */}
        <div className="relative h-[500px] lg:h-[600px]">
          {/* Chaos state */}
          <div className={`absolute inset-0 transition-all duration-1000 ${animationStage >= 2 && animationStage < 4 ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            <div className="relative h-full">
              {/* Scattered sticky notes */}
              {chaosState.tasks.map((task, index) => (
                <div
                  key={task.id}
                  className={`absolute px-3 py-2 bg-yellow-100 border border-yellow-300 rounded shadow-sm transform rotate-${index % 2 === 0 ? '3' : '-3'} transition-all duration-500`}
                  style={{
                    left: `${task.x}%`,
                    top: `${task.y}%`,
                    transitionDelay: `${index * 50}ms`
                  }}
                >
                  <div className="flex items-start gap-2">
                    {task.urgent && <AlertCircle className="w-3 h-3 text-red-500 mt-0.5" />}
                    <span className="text-xs font-medium text-slate-700">{task.text}</span>
                  </div>
                </div>
              ))}

              {/* Stress indicator */}
              <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Sheppard's Stress Level</span>
                  <span className="text-sm font-bold text-red-600">{chaosState.metrics.stress}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-1000"
                    style={{ width: `${chaosState.metrics.stress}%` }}
                  />
                </div>
                <div className="flex justify-between mt-3 text-xs text-slate-500">
                  <span>{chaosState.metrics.tasks} tasks</span>
                  <span>{chaosState.metrics.overdue} overdue</span>
                </div>
              </div>
            </div>
          </div>

          {/* Organized state */}
          <div className={`absolute inset-0 transition-all duration-1000 ${animationStage >= 4 ? 'opacity-100 scale-100' : 'opacity-0 scale-110'}`}>
            <div className="h-full flex flex-col gap-4 p-4">
              {/* Today's focus card */}
              <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-azure-500">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Today's Focus</h3>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                    On Track
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-azure-50 rounded-lg">
                    <div className="w-8 h-8 bg-azure-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <span className="font-medium text-slate-900">Complete profile foundation</span>
                    <span className="ml-auto text-sm text-slate-500">2.5h</span>
                  </div>
                  <div className="text-sm text-slate-600">
                    ✨ This unlocks 12 dependent tasks and auto-populates your brag sheet
                  </div>
                </div>
              </div>

              {/* Smart batches preview */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-slate-900">Essay Sprint</span>
                  </div>
                  <span className="text-xs text-slate-600">4 tasks → 1 session</span>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-medium text-slate-900">Quick Wins</span>
                  </div>
                  <span className="text-xs text-slate-600">6 tasks → 45 min</span>
                </div>
              </div>

              {/* New stress level */}
              <div className="bg-white rounded-lg shadow-lg p-4 mt-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Sheppard's Stress Level</span>
                  <span className="text-sm font-bold text-emerald-600">{organizedState.metrics.stress}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-1000"
                    style={{ width: `${organizedState.metrics.stress}%` }}
                  />
                </div>
                <div className="flex justify-between mt-3 text-xs text-slate-500">
                  <span>{organizedState.metrics.tasks} smart sessions</span>
                  <span className="text-emerald-600">All on track ✓</span>
                </div>
              </div>
            </div>
          </div>

          {/* Transformation indicator */}
          {animationStage === 3 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-full p-8 shadow-2xl animate-ping">
                <Sparkles className="w-12 h-12 text-azure-600" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;